/**
 * UAT 2026-05-13 F-D4 — Project monthly overage approval API client.
 *
 * Surfaces the Phase 37 Plan 37-06 backend service
 * (`backend/src/services/projectMonthlyOverageApprovalService.ts`) on the
 * frontend; the v7.0 PROJECT.md core value explicitly mandates "PM can record
 * 'Schválené hodiny nad rámec pro tento měsíc'" but no UI was wired in Phase
 * 41 — this client closes that gap.
 *
 * Authorization (defense-in-depth on top of backend rbac):
 *   - GET list                    → requireAdminOrProjectMemberByProject
 *   - POST upsert / PATCH update  → requireAdminOrProjectPM
 *   - No DELETE — history is append-only.
 */
import { apiGet, apiPost, apiPatch } from './client';

export interface MonthlyOverageApproval {
  id: number;
  projectId: number;
  /** 'YYYY-MM' — month the approval applies to. */
  periodYearMonth: string;
  /**
   * Decimal serialized as "12.50", or `null` when PM acknowledged the
   * overage without committing to a count (2026-05-18).
   */
  approvedHours: string | null;
  approvedById: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertMonthlyOverageApprovalInput {
  periodYearMonth: string;
  /** Decimal string matching ^[0-9]+(\.[0-9]{1,2})?$, or `null` to acknowledge only. */
  approvedHours: string | null;
}

export interface UpdateMonthlyOverageApprovalInput {
  approvedHours: string | null;
}

export async function listMonthlyOverageApprovals(
  projectId: number,
): Promise<MonthlyOverageApproval[]> {
  const res = await apiGet<{ data: MonthlyOverageApproval[] }>(
    `/projects/${projectId}/monthly-overage-approvals`,
  );
  return res.data;
}

export async function upsertMonthlyOverageApproval(
  projectId: number,
  input: UpsertMonthlyOverageApprovalInput,
): Promise<MonthlyOverageApproval> {
  const res = await apiPost<{ data: MonthlyOverageApproval }>(
    `/projects/${projectId}/monthly-overage-approvals`,
    input,
  );
  return res.data;
}

export async function updateMonthlyOverageApproval(
  projectId: number,
  approvalId: number,
  input: UpdateMonthlyOverageApprovalInput,
): Promise<MonthlyOverageApproval> {
  const res = await apiPatch<{ data: MonthlyOverageApproval }>(
    `/projects/${projectId}/monthly-overage-approvals/${approvalId}`,
    input,
  );
  return res.data;
}
